/**
 * Braintree 3-D Secure handler (client-SDK driven, Q-BRAINTREE-08).
 *
 * WHY the braintree-web SDK and NOT the shared show3DSIframeModal (VGS/Airwallex) or a
 * redirect: Braintree 3DS2 is driven entirely by the `braintree-web` threeDSecure
 * component + Cardinal/Songbird in the browser — there is no redirect URL that posts back
 * to OUR 3ds-callback, and no server poll. The component renders the Cardinal ACS step-up
 * itself and, on completion, hands us an AUTHENTICATED single-use nonce. The card loop
 * round-trips that nonce to `cardCheckoutVerify`, where the backend charges it
 * (`chargeCreditCard`) — exactly as Adyen native 3DS2 round-trips its `state.data`.
 *
 * Loaded GLOBALLY from Braintree's CDN (like Stripe.js / Adyen Web — `window.braintree`),
 * NOT bundled: no new package.json / bundle-size commitment, and the dep-vs-bundle call
 * stays Augustin's (CLAUDE.md: frontend + dependency approval). The merchant adds the two
 * braintree-web scripts (client + three-d-secure) to their <head>, exactly as they already
 * do for Stripe.js / Adyen Web.
 *
 * FRONTEND NOTE: this is an Augustin-gated FE change (pre-pilot handoff) — built + flagged
 * for his review, NOT merged without him.
 */

import type { PublicCardCheckoutResponse } from "@pkg/sdk/models";
import type { NextActionResult } from "./next-action-handlers";

// Pin to the version the spike + capability E2E proved against the real Cardinal sandbox
// (client.min.js + three-d-secure.min.js 3.118.2). Must match the <script> tags in the
// embedder's <head> (see frontend/apps/playground/index.html).
const BRAINTREE_WEB_VERSION = "3.118.2";

// Guard only the PRE-CHALLENGE Cardinal lookup so a silent / stalled lookup can't hang
// checkout forever. Cleared in onLookupComplete (below) BEFORE the interactive challenge
// renders, so a legitimately slow shopper typing an OTP in the Cardinal step-up is never
// aborted. Mirrors the Adyen native-3DS fingerprint-only guard (ADYEN_FINGERPRINT_TIMEOUT_MS):
// the invisible phase is time-bounded, the interactive challenge is not.
const VERIFY_CARD_TIMEOUT_MS = 20_000;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Minimal shape of the `braintree-web` 3DS2 result we consume. */
type ThreeDSecureVerifyResult = {
  nonce: string;
  liabilityShifted?: boolean;
  threeDSecureInfo?: { status?: string };
};

/** Minimal shape of the `braintree-web` component instances we drive. */
type BraintreeClientInstance = {
  // braintree-web clients expose teardown() to release the resources create() set up;
  // optional so a CDN build that omits it is handled gracefully.
  teardown?: () => Promise<void>;
};
type ThreeDSecureInstance = {
  verifyCard: (options: {
    nonce: string;
    amount: string;
    bin?: string;
    onLookupComplete?: (data: unknown, next: () => void) => void;
  }) => Promise<ThreeDSecureVerifyResult>;
  // threeDSecure v2 installs Cardinal/Songbird global window listeners on create; tearing
  // it down removes them so a same-page retry does not re-init Cardinal and leak listeners.
  teardown: () => Promise<void>;
};

/** The global `braintree` namespace the CDN build installs. */
type BraintreeGlobal = {
  client: { create: (opts: { authorization: string }) => Promise<BraintreeClientInstance> };
  threeDSecure: {
    create: (opts: { client: BraintreeClientInstance; version: number }) => Promise<ThreeDSecureInstance>;
  };
};

const isBraintreeWebPresent = (): boolean =>
  typeof window !== "undefined" &&
  "braintree" in window &&
  typeof (window as { braintree?: Partial<BraintreeGlobal> }).braintree?.client?.create === "function" &&
  typeof (window as { braintree?: Partial<BraintreeGlobal> }).braintree?.threeDSecure?.create === "function";

/**
 * Get the global `braintree` namespace (loaded via CDN script tags, mirroring
 * `getLoadedStripe` / `getLoadedAdyen`). Polls for up to 5 seconds.
 */
const getLoadedBraintree = async (): Promise<BraintreeGlobal> => {
  for (let i = 0; i < 10; i++) {
    if (isBraintreeWebPresent()) {
      break;
    }
    await sleep(500);
  }

  if (!isBraintreeWebPresent()) {
    throw new Error(
      "braintree-web not loaded. 3DS needs the braintree-web client + three-d-secure SDK. " +
        "Add these to your HTML <head>:\n" +
        `<script src="https://js.braintreegateway.com/web/${BRAINTREE_WEB_VERSION}/js/client.min.js"></script>\n` +
        `<script src="https://js.braintreegateway.com/web/${BRAINTREE_WEB_VERSION}/js/three-d-secure.min.js"></script>`,
    );
  }

  return (window as unknown as { braintree: BraintreeGlobal }).braintree;
};

/**
 * Handle a Braintree 3DS next action.
 *
 * Initializes braintree-web with the backend's CUSTOMER-SCOPED client token, creates the
 * threeDSecure v2 component, and runs `verifyCard({ nonce, bin, amount })` on the
 * vaulted-card nonce. braintree-web auto-mounts and drives the Cardinal ACS step-up modal
 * to completion; on success it resolves with an AUTHENTICATED nonce, which we return INSIDE
 * the generic `details` carrier as
 * `NextActionResult.data.details.braintree_authenticated_nonce` for the card loop to forward
 * to `cardCheckoutVerify` (where the backend charges it). This is the same slug-free
 * `{ details }` shape Adyen native 3DS2 uses, so the shared card loop special-cases no
 * processor.
 */
export const handleBraintree3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "braintree_3ds" }>,
): Promise<NextActionResult> => {
  const { clientToken, nonce, amount } = nextAction;
  const bin = typeof nextAction.bin === "string" ? nextAction.bin : undefined;

  if (!clientToken || !nonce || !amount) {
    console.error("[3DS:Braintree] Missing clientToken/nonce/amount on the action.", nextAction.type);
    return { success: false, error: "Braintree 3DS is not fully configured." };
  }

  let braintree: BraintreeGlobal;
  try {
    braintree = await getLoadedBraintree();
  } catch (error) {
    console.error("[3DS:Braintree]", error);
    return { success: false, error: error instanceof Error ? error.message : "braintree-web not loaded" };
  }

  let client: BraintreeClientInstance | undefined;
  let threeDS: ThreeDSecureInstance | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  // Distinct sentinel so a verifyCard result and the timeout are never confused.
  const TIMED_OUT = Symbol("braintree-3ds-timeout");
  try {
    client = await braintree.client.create({ authorization: clientToken });
    threeDS = await braintree.threeDSecure.create({ client, version: 2 });

    // Race ONLY the pre-challenge lookup against a timeout so a silent / never-completing
    // Cardinal lookup can't hang checkout. Once the lookup resolves (onLookupComplete) we
    // clear the timer BEFORE draining into the challenge, so the interactive OTP step-up —
    // where a real shopper may legitimately take well over 20s to type a code — is never
    // time-bounded and never spuriously aborted. A stalled lookup (onLookupComplete never
    // fires) still trips the timeout. The timeout resolves (never rejects) so the finally
    // block always runs and tears the braintree SDK down.
    const result = await Promise.race([
      threeDS.verifyCard({
        nonce,
        amount,
        bin,
        // v2 requires draining the lookup before the challenge renders. The lookup is the
        // only time-bounded phase: clear the hang-guard here, then render the challenge.
        onLookupComplete: (_data, next) => {
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
          }
          next();
        },
      }),
      new Promise<typeof TIMED_OUT>((resolve) => {
        timeoutId = setTimeout(() => resolve(TIMED_OUT), VERIFY_CARD_TIMEOUT_MS);
      }),
    ]);

    if (result === TIMED_OUT) {
      console.error("[3DS:Braintree] verifyCard timed out");
      return { success: false, error: "3-D Secure timed out" };
    }

    if (!result?.nonce) {
      // Do NOT log the full result — it carries the single-use authenticated nonce.
      console.error("[3DS:Braintree] verifyCard returned no authenticated nonce");
      return { success: false, error: "3DS authentication did not complete" };
    }

    // Round-trip the authenticated nonce to the backend verify (the source of truth for
    // the final charge outcome) INSIDE the generic details carrier; liabilityShifted is
    // informational only here.
    return { success: true, data: { details: { braintree_authenticated_nonce: result.nonce } } };
  } catch (error) {
    // Do NOT log the raw error object — on some failure paths braintree-web attaches the
    // verify payload (including the nonce). Log a redacted message only.
    const message =
      error instanceof Error ? error.message : typeof error === "string" ? error : "3DS authentication failed";
    console.error("[3DS:Braintree] verifyCard failed:", message);
    return { success: false, error: message };
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    // Always tear down the threeDSecure instance (even on the success path) so Cardinal's
    // global window listeners are removed; otherwise a same-page retry re-inits Cardinal and
    // leaks listeners. Teardown failures are non-fatal — the next create() re-initializes.
    if (threeDS) {
      try {
        await threeDS.teardown();
      } catch (teardownError) {
        console.warn("[3DS:Braintree] threeDSecure teardown failed (non-fatal):", teardownError);
      }
    }
    // Also tear down the client so the resources create() set up are released.
    if (client?.teardown) {
      try {
        await client.teardown();
      } catch (teardownError) {
        console.warn("[3DS:Braintree] client teardown failed (non-fatal):", teardownError);
      }
    }
  }
};
